import React, { useCallback, useEffect, useState } from "react"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import type { ScheduledWorkflow } from "@/reducers/openclawWorkflowsSlice"
import { messageOf, phaseOf, StopPhase } from "@/lib/stopAutomations"
import {
  deleteStopAutomation,
  updateStopAutomation,
} from "@/api/xeps/StopAutomations"
import { useAutomationMutation } from "@/hooks/useAutomationMutation"
import { StopAutomationFields } from "./StopAutomationFields"
import { useTranslation } from "react-i18next"

export type StopAutomationEditDialogProps = {
  /** The rule being edited; null closes the dialog. */
  job: ScheduledWorkflow | null
  onOpenChange: (open: boolean) => void
}

/**
 * Editing one existing per-stop rule from the automations list.
 *
 * The route panel's dialog is the place to *create* a rule — it is what knows
 * the stop's identity. Here the rule already carries that identity, so only
 * the two fields the dispatcher actually chose are editable: the instruction
 * and the phase. Everything else would make it a different rule.
 */
export const StopAutomationEditDialog: React.FC<
  StopAutomationEditDialogProps
> = ({ job, onOpenChange }) => {
  const { t } = useTranslation()
  const [phase, setPhase] = useState<StopPhase>("arrival")
  const [message, setMessage] = useState("")
  const { saving, error, setError, run } = useAutomationMutation()

  // Opening for another rule must not inherit the previous rule's draft.
  useEffect(() => {
    if (!job) return
    setPhase(phaseOf(job) ?? "arrival")
    setMessage(messageOf(job))
    setError(null)
  }, [job, setError])

  const save = useCallback(async () => {
    const trimmed = message.trim()
    if (!job || !trimmed) return

    const ok = await run((client) =>
      updateStopAutomation(client, job.id, {
        message: trimmed,
        phase,
        name: job.name,
      }),
    )
    if (ok) onOpenChange(false)
  }, [job, message, onOpenChange, phase, run])

  const remove = useCallback(async () => {
    if (!job) return
    const ok = await run((client) => deleteStopAutomation(client, job.id))
    if (ok) onOpenChange(false)
  }, [job, onOpenChange, run])

  return (
    <Dialog open={job !== null} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="truncate">{job?.name}</DialogTitle>
          <DialogDescription>
            {t("maps.stopAutomationEditDialog.description")}
          </DialogDescription>
        </DialogHeader>

        <StopAutomationFields
          phase={phase}
          onPhaseChange={setPhase}
          message={message}
          onMessageChange={setMessage}
          disabled={saving}
          error={error}
          fieldId="stop-automation-edit-message"
        />

        <DialogFooter className="sm:justify-between">
          <Button
            variant="ghost"
            className="text-red-500 hover:text-red-600"
            disabled={saving}
            onClick={remove}
          >
            {t("common.delete")}
          </Button>
          <Button onClick={save} disabled={saving || !message.trim()}>
            {t("common.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default StopAutomationEditDialog
